// iter414 — Bulk auto-promote stale rules dialog.
// Triggered from the SmartReconcile widget when stale (≥7d unassigned)
// txns share description patterns. Calls /banking/rules/suggest with
// stale_only=true to get high-confidence suggestions, lets the user
// tick which ones to promote, then bulk-creates via /banking/rules/bulk-create.
//
// Why this matters: a tenant with 246 stale txns can have just 8-10
// recurring patterns (STC, Visa, Internal Transfer…). Promoting those
// to auto-rules clears the backlog in one click and ensures future
// occurrences self-resolve.
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, AlertCircle, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SR = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function StaleRulePromoteDialog({ open, onOpenChange, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [scanned, setScanned] = useState(0);
  const [picked, setPicked] = useState(() => new Set());
  // iter415 — When true, after bulk-create succeeds we also call
  // /banking/rules/replay with the just-created rule_ids so the
  // existing stale txns get auto-resolved retroactively. Without this,
  // new rules only catch FUTURE matches and the backlog stays stuck.
  const [alsoReplay, setAlsoReplay] = useState(true);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      // 365-day window so we cover deep backlogs. min_count=3 keeps the
      // bar high enough that auto-promotion doesn't create accidental
      // bad rules.
      const r = await api.post('/banking/rules/suggest?days=365&min_count=3&stale_only=true&limit=20');
      const items = r.data?.suggestions || [];
      setSuggestions(items);
      setScanned(r.data?.scanned || 0);
      // Pre-tick the top 5 (highest confidence) so the user just clicks Create.
      setPicked(new Set(items.slice(0, 5).map((s) => s.canonical)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to scan stale txns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchSuggestions();
  }, [open]);

  const togglePick = (canonical) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(canonical)) next.delete(canonical);
      else next.add(canonical);
      return next;
    });
  };

  const pickAll = () => setPicked(new Set(suggestions.map((s) => s.canonical)));
  const pickNone = () => setPicked(new Set());

  const create = async () => {
    if (picked.size === 0) return;
    setCreating(true);
    try {
      // Map the suggestion shape into BankingRuleCreate. We hard-set
      // kind='expense' + match_type='contains' + category='Uncategorized'
      // — the user can refine each rule afterwards in the rules table.
      const rules = suggestions
        .filter((s) => picked.has(s.canonical))
        .map((s) => ({
          name: s.suggested_name || `${s.token} auto-rule`,
          match_type: 'contains',
          match_value: s.suggested_match_value || s.token,
          direction: s.direction === 'incoming' ? 'incoming' : 'outgoing',
          kind: s.direction === 'incoming' ? 'incoming_other' : 'expense',
          vendor_name: s.token,
          category_id: null,
          enabled: true,
          priority: 50,
        }));
      const r = await api.post('/banking/rules/bulk-create', { rules });
      const created = r.data?.created || [];
      const n = created.length || rules.length;

      // iter415 — Optionally replay the newly-created rules across the
      // last 365 days so existing stale txns get auto-categorised.
      // Without this, the user sees "5 rules created" but the 569
      // already-stale txns still sit in the backlog until they happen
      // to recur — defeats the purpose of bulk promotion.
      let replayMsg = '';
      if (alsoReplay && created.length > 0) {
        try {
          const ids = created.map((c) => c.id).join(',');
          const replay = await api.post(
            `/banking/rules/replay?days=365&apply=true&rule_ids=${encodeURIComponent(ids)}`,
          );
          const matched = replay.data?.matched || replay.data?.matched_total || 0;
          const applied = replay.data?.applied || replay.data?.applied_total || 0;
          if (matched > 0) {
            replayMsg = ` · matched ${matched} stale txn${matched === 1 ? '' : 's'}` +
              (applied > 0 ? ` (${applied} expense doc${applied === 1 ? '' : 's'} auto-created)` : '');
          }
        } catch (e) {
          // Non-fatal — rules were created successfully even if replay
          // failed. Just toast a softer warning.
          toast.warning(`Rules created but replay failed: ${e?.response?.data?.detail || e.message}`);
        }
      }
      toast.success(`Created ${n} auto-rule${n === 1 ? '' : 's'}${replayMsg}`);
      onOpenChange(false);
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk create failed');
    } finally {
      setCreating(false);
    }
  };

  const totalCovered = suggestions
    .filter((s) => picked.has(s.canonical))
    .reduce((acc, s) => acc + (s.count || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="stale-rule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-emerald-600" />
            Bulk auto-promote stale rules
          </DialogTitle>
          <DialogDescription>
            We scanned <b>{scanned.toLocaleString()}</b> bank-statement txns and
            grouped the stale (≥7-day-unassigned) ones by description pattern.
            Pick the patterns you want to auto-categorise — each ticked row
            becomes a permanent rule. Future txns matching the pattern resolve
            on their own.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-xs text-muted-foreground">Scanning stale txns…</div>
        ) : suggestions.length === 0 ? (
          <div className="py-8 text-center text-xs text-muted-foreground flex flex-col items-center gap-2" data-testid="stale-rule-empty">
            <AlertCircle size={20} />
            No high-confidence patterns found. Either every stale txn is unique
            or existing rules already cover them.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">
                {picked.size} of {suggestions.length} selected
                {totalCovered > 0 && ` · covers ${totalCovered} stale txns`}
              </span>
              <Button variant="ghost" size="sm" className="h-6 text-[10px] ml-auto" onClick={pickAll} data-testid="stale-rule-pick-all">All</Button>
              <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={pickNone} data-testid="stale-rule-pick-none">None</Button>
            </div>
            <div className="max-h-[60dvh] overflow-y-auto -mx-2 px-2">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b sticky top-0 bg-background">
                    <tr>
                      <th className="w-6"></th>
                      <th className="text-left py-2 px-2">Pattern</th>
                      <th className="text-right py-2 px-2">Hits</th>
                      <th className="text-right py-2 px-2">Total (SR)</th>
                      <th className="text-center py-2 px-2">Direction</th>
                      <th className="text-center py-2 px-2">Confidence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {suggestions.map((s) => {
                      const checked = picked.has(s.canonical);
                      return (
                        <tr
                          key={s.canonical}
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => togglePick(s.canonical)}
                          data-testid={`stale-rule-row-${s.canonical}`}
                        >
                          <td className="py-2 px-1 text-center align-top">
                            {checked
                              ? <CheckSquare size={14} className="text-emerald-600 inline" />
                              : <Square size={14} className="text-muted-foreground inline" />}
                          </td>
                          <td className="py-2 px-2 align-top">
                            <div className="font-semibold">{s.token}</div>
                            {s.merged_variants?.length > 0 && (
                              <div className="text-[10px] text-muted-foreground">
                                also covers: {s.merged_variants.slice(0, 3).join(', ')}
                              </div>
                            )}
                            {s.samples?.[0] && (
                              <div className="text-[10px] text-muted-foreground/70 truncate max-w-[280px]" title={s.samples[0]?.description || ''}>
                                e.g. {s.samples[0]?.description || s.samples[0]?.amount || ''}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums font-semibold">{s.count}</td>
                          <td className="py-2 px-2 text-right tabular-nums">{SR(s.total_amount)}</td>
                          <td className="py-2 px-2 text-center">
                            <Badge variant="outline" className="text-[9px] uppercase">
                              {s.direction || 'out'}
                            </Badge>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <span className={`text-xs font-bold ${s.confidence >= 80 ? 'text-emerald-700' : s.confidence >= 60 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                              {s.confidence}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <DialogFooter className="flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* iter415 — Replay checkbox. Pre-ticked because clearing the
              backlog is the whole point of bulk-promotion; users
              opting OUT must explicitly uncheck. */}
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none mr-auto">
            <input
              type="checkbox"
              checked={alsoReplay}
              onChange={(e) => setAlsoReplay(e.target.checked)}
              className="rounded border-stone-300"
              data-testid="stale-rule-also-replay"
            />
            <span className="text-stone-700">
              Also apply rules to existing stale txns (recommended)
            </span>
          </label>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="stale-rule-cancel">
              Cancel
            </Button>
            <Button
              onClick={create}
              disabled={picked.size === 0 || creating}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="stale-rule-create"
            >
              {creating ? 'Creating…' : `Create ${picked.size} rule${picked.size === 1 ? '' : 's'}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default StaleRulePromoteDialog;
